import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { getCoinDetails, getCoinTickers } from "../services/api";
import CryptoChart from "../components/CryptoChart";
import {
  BsReddit,
  BsCodeSlash,
  BsGlobe,
  BsExclamationCircle,
  BsDatabaseFillExclamation,
} from "react-icons/bs";
import { TbCameraOff } from "react-icons/tb";
import {
  FaExternalLinkAlt,
  FaYoutube,
  FaDollarSign,
  FaSortNumericDown,
  FaCalendarAlt,
  FaCog,
  FaBookmark,
  FaRegBookmark,
} from "react-icons/fa";

const getStartOfYear = () => {
  const now = new Date();
  return new Date(now.getFullYear(), 0, 1)
    .toISOString()
    .split("T")[0];
};
const getStatusBadge = (isActive) => (
  <span
    className={`px-2 py-1 uppercase font-semibold border-2 rounded-full ${
      isActive
        ? "border-blue-500 text-blue-500"
        : "border-red-500 text-red-500"
    }`}
  >
    {isActive ? "active" : "inactive"}
  </span>
);
const formatDate = (dateString) => {
  const options = { year: "numeric", month: "long", day: "numeric" };
  return new Date(dateString).toLocaleDateString(undefined, options);
};
const IconLink = ({ icon, text, link }) => {
  if (!link) return null;

  return (
    <div className="flex items-center mb-2">
      {icon}
      <a
        href={link}
        target="_blank"
        rel="noreferrer"
        className="ml-2 text-blue-500 transition-all border-b-2 border-transparent hover:text-blue-700 hover:border-blue-700"
      >
        {text}
      </a>
    </div>
  );
};
const CryptocurrenciesDetail = () => {
  const { id } = useParams();
  const [cryptoDetails, setCryptoDetails] = useState(null);
  const [coinPrice, setCoinPrice] = useState(null);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const payload = {
    historical: {
      start: getStartOfYear(),
      end: Date.now(),
      interval: "1d",
    },
  };
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [detailsResponse, priceResponse] = await Promise.all([
          getCoinDetails(id),
          getCoinTickers(id),
        ]);
        setCryptoDetails(detailsResponse.data);
        setCoinPrice(priceResponse.data);
        const isCoinBookmarked = localStorage.getItem(`bookmark_${id}`);
        setIsBookmarked(!!isCoinBookmarked);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, [id]);
  const handleBookmark = () => {
    localStorage.setItem(`bookmark_${id}`, JSON.stringify(cryptoDetails));
    setIsBookmarked(true);
  };
  const handleUnbookmark = () => {
    localStorage.removeItem(`bookmark_${id}`);
    setIsBookmarked(false);
  };
  if (!cryptoDetails) {
    return (
      <div className="container mx-auto mt-8 text-center">
        Loading...
      </div>
    );
  }
  return (
    <div className="container mx-auto mt-8">
      <div className="p-4">
        {/* Header */}
        <div className="flex items-center mb-4 space-x-4">
          <p className="text-2xl italic font-bold uppercase">
            {cryptoDetails.name} ({cryptoDetails.symbol})
          </p>
          {getStatusBadge(cryptoDetails.is_active)}
          {isBookmarked ? (
            <FaBookmark
              className="w-6 h-6 text-blue-500 cursor-pointer"
              onClick={handleUnbookmark}
            />
          ) : (
            <FaRegBookmark
              className="w-6 h-6 text-blue-500 cursor-pointer"
              onClick={handleBookmark}
            />
          )}
        </div>
        <div className="flex-row md:flex">
          <div>
            {coinPrice?.quotes?.USD && (
              <p className="flex items-center mb-2">
                <FaDollarSign className="mr-2 text-blue-500" />
                Price in USD:
                <span className="ml-2 font-bold">
                  {coinPrice.quotes.USD.price.toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                    minimumFractionDigits: 3,
                    maximumFractionDigits: 3,
                  })}
                </span>
              </p>
            )}
            {coinPrice?.circulating_supply && (
              <p className="flex items-center mb-2">
                <BsDatabaseFillExclamation className="mr-2 text-blue-500" />
                Circulating Supply:
                <span className="ml-2 font-bold">
                  {coinPrice.circulating_supply.toLocaleString("en-US")}
                </span>
              </p>
            )}
            <p className="flex items-center mb-2">
              <FaSortNumericDown className="mr-2 text-blue-500" />
              Rank:
              <span className="ml-2 font-bold">{cryptoDetails.rank}</span>
            </p>
            <p className="flex items-center mb-2">
              <FaCalendarAlt className="mr-2 text-blue-500" />
              Start Date:
              <span className="ml-2 font-bold">
                {formatDate(cryptoDetails.started_at)}
              </span>
            </p>
            <p className="flex items-center mb-2">
              <FaCog className="mr-2 text-blue-500" />
              Development status:
              <span className="ml-2 font-bold">
                {cryptoDetails.development_status}
              </span>
            </p>
            <div className="my-6 md:mr-52">
              <p className="font-bold text-blue-500">Description</p>
              <p>{cryptoDetails.description}</p>
            </div>
          </div>
          {cryptoDetails.logo ? (
            <img
              src={cryptoDetails.logo}
              alt={`${cryptoDetails.name} logo`}
              className="
                w-full 
                max-w-[200px] 
                sm:max-w-[240px] 
                md:max-w-[280px] 
                lg:max-w-[320px]
                h-auto
                mx-auto
                md:mx-0
                mb-6
                md:mb-0
              "
            />
          ) : (
            <p className="italic font-light">
              <TbCameraOff /> No image is available
            </p>
          )}
        </div>
        {/* Links */}
        <div className="flex flex-wrap my-12 justify-evenly md:justify-between">
          <IconLink icon={<BsGlobe />} text="Website" link={cryptoDetails.links?.website} />
          <IconLink icon={<BsCodeSlash />} text="Source Code" link={cryptoDetails.links?.source_code} />
          <IconLink icon={<FaExternalLinkAlt />} text="Explorer" link={cryptoDetails.links?.explorer} />
          <IconLink icon={<BsReddit />} text="Reddit" link={cryptoDetails.links?.reddit} />
          <IconLink icon={<FaYoutube />} text="Youtube" link={cryptoDetails.links?.youtube} />
        </div>
        <CryptoChart
          id={id}
          interval={payload.historical.interval}
          start={payload.historical.start}
        />
      </div>
      {/* Team & Whitepaper */}
      <div className="flex-row mt-8 md:flex md:justify-between">
        {cryptoDetails.team?.length ? (
          <div className="md:w-3/4">
            <p className="mb-4 font-bold text-blue-500">Team</p>
            {cryptoDetails.team.map((member, index) => (
              <p key={index}>
                <span className="font-bold">{member.name}</span> – {member.position}
              </p>
            ))}
          </div>
        ) : (
          <p className="italic font-light">
            <BsExclamationCircle className="inline mr-2" />
            No Team Information Available
          </p>
        )}
        {cryptoDetails.whitepaper ? (
          <div className="md:w-1/4">
            <p className="my-4 font-bold text-blue-500 md:mt-0">Whitepaper</p>
            <a
              href={cryptoDetails.whitepaper.link}
              target="_blank"
              rel="noreferrer"
            >
              <img
                src={cryptoDetails.whitepaper.thumbnail}
                alt="Whitepaper Thumbnail"
                width={350}
                height={350}
              />
            </a>
          </div>
        ) : (
          <p className="italic font-light">
            <BsExclamationCircle className="inline mr-2" />
            No Whitepaper Available
          </p>
        )}
      </div>
    </div>
  );
};
export default CryptocurrenciesDetail;
